/**
 * `/gst-returns` — GSTR-3B preparation (S33).
 *
 * V1.0 ships a read-only surface for the CA to take to the portal.
 * system-decisions §9: Flint prepares, CA files. No "push to GSTN".
 *
 * Chrome mirrors `prototype/compliance/gst-returns.html` section-for-section:
 *   · Page head + 4 action buttons (Sync GSTN V1.x, Settings real, Record
 *     payment V1.x, Push to GSTN V1.x)
 *   · GSTIN profile strip — name, GSTIN, state, Regular-taxpayer pill,
 *     Scheme/Registered/E-invoice mandate (V1.x placeholders) + 2 mini
 *     buttons (View on portal, Change scheme — both V1.x)
 *   · Period strip (6 months) — real
 *   · KPI strip — Net payable (real) + ITC available (real) + E-invoices
 *     (V1.x) + On-time score (V1.x)
 *   · Coverage banner — real, names the supported/unsupported GSTR-3B lines
 *   · Returns grid — 4 cards (GSTR-3B real, GSTR-1/2B/9 V1.x)
 *   · GSTR-3B breakdown table — real, line-by-line aggregation
 *   · ITC reconciliation card (V1.x) — tabs + empty state
 *   · Right rail: E-invoice strip (V1.x) + Tax summary panel (real, built
 *     from breakdown) + HSN summary mini-table (V1.x)
 *   · Filing history strip (V1.x) — 12-month pip grid
 *
 * Every V1.x sub-surface keeps the rich chrome so the user sees the
 * workflow that will land — same honesty seam as `unsupportedCodes`.
 *
 * @see prototype/compliance/gst-returns.html — V1.1 design baseline (Studio Slate);
 *   the rich one. Do not introduce a parallel "v1.0-only" prototype file —
 *   slice from this one and disable V1.x bits.
 * @see docs/spec/08-system-decisions.md §9 — compliance posture (CA files)
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock3,
  Cloud,
  Info,
  Minus,
  PieChart,
  Receipt,
  Send,
  Settings,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { Alert } from "@flint/ui";
import type { gstReturns as gstReturnsContract } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { formatDate } from "../../../../lib/format.ts";

export const Route = createFileRoute("/(workspace)/compliance/gst-returns/")({
  component: GstReturnsPage,
});

function GstReturnsPage() {
  // Period state — local. URL-search-params upgrade is V1.0.x.
  const [period, setPeriod] = useState(() => currentMonthIso());

  const summary = trpc.gstReturns.getMonthSummary.useQuery({ period });
  const data = summary.data;

  // FY months strip — show 6 months ending at the selected period.
  const months = useMemo(() => buildMonthStrip(period), [period]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Compliance" }, { label: "GSTR-3B preparation" }]}>
      <div className="flex flex-col gap-[18px]">
        {/* Page head */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              GSTR-3B preparation
            </h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
              <span>
                <strong className="text-ink">{monthLabel(period)}</strong>
              </span>
              <Sep />
              {data ? (
                <>
                  <span>
                    <span className="num text-ink font-semibold">{data.totals.invoiceCount}</span>{" "}
                    invoices ·{" "}
                    <span className="num text-ink font-semibold">{data.totals.billCount}</span>{" "}
                    bills
                  </span>
                  <Sep />
                  <span>
                    Due <strong className="text-rose-700">{formatDate(data.dueOn)}</strong>
                    {" · "}
                    <DaysToDue dueOn={data.dueOn} />
                  </span>
                </>
              ) : null}
            </p>
          </div>
          {/* Page-head actions — mirror `prototype/compliance/gst-returns.html:935-952`.
              Sync from GSTN / Push to GSTN are V1.x (adapter not yet wired).
              Settings is functional (links to /settings). Record payment is
              V1.x (cash-movement vertical hasn't shipped). */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <DisabledButton tone="secondary" tooltip="GSTN sync lands in V1.x">
              <Cloud size={13} strokeWidth={2} />
              Sync from GSTN
            </DisabledButton>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-paper text-ink-2 border border-line hover:bg-bg hover:border-line-2 transition-colors no-underline"
            >
              <Settings size={13} strokeWidth={2} />
              Settings
            </Link>
            <DisabledButton tone="secondary" tooltip="Statutory-payment recording lands in V1.x">
              <Send size={13} strokeWidth={2} />
              Record payment
            </DisabledButton>
            <DisabledButton tone="primary" tooltip="Push to GSTN lands in V1.x">
              Push to GSTN
            </DisabledButton>
          </div>
        </header>

        {/* GSTIN profile strip — mirrors `prototype/compliance/gst-returns.html:955-973`.
            Scheme (QRMP vs Monthly), Registered date, and "E-invoice mandatory"
            are V1.x — they render as muted placeholders so the chrome matches
            and the user sees where the data will land. */}
        {data ? (
          <div className="flex items-center gap-4 px-4 py-3 bg-paper border border-line rounded-md shadow-sm flex-wrap">
            <span className="inline-grid place-items-center w-9 h-9 rounded-sm bg-sky-50 text-sky-600 flex-shrink-0">
              <ShieldCheck size={16} strokeWidth={2} />
            </span>
            <div className="flex gap-3.5 flex-wrap items-center text-xs text-muted flex-1 min-w-0">
              <strong className="text-ink font-semibold">{data.company.legalName}</strong>
              <span className="font-mono text-ink-2 font-semibold px-2 py-0.5 bg-bg rounded-sm">
                {data.company.gstin}
              </span>
              {data.company.state ? (
                <span>
                  {data.company.state}
                  {data.company.stateCode ? ` · state ${data.company.stateCode}` : ""}
                </span>
              ) : null}
              <span className="inline-flex px-2 py-0.5 bg-sky-50 text-sky-700 rounded-sm text-xs font-semibold">
                Regular taxpayer
              </span>
              {/* Scheme · QRMP vs Monthly — V1.x (no scheme field on `companies` yet). */}
              <span className="inline-flex items-center gap-1.5">
                Scheme
                <span
                  className="text-muted font-medium px-1.5 py-0.5 bg-bg rounded-sm border border-dashed border-line-2 text-xs"
                  title="Lands once company scheme is captured · V1.x"
                >
                  Monthly · V1.x
                </span>
              </span>
              {/* Registered date — V1.x (no `companies.gstin_registered_on` column). */}
              <span className="inline-flex items-center gap-1.5">
                Registered
                <span
                  className="text-muted font-medium px-1.5 py-0.5 bg-bg rounded-sm border border-dashed border-line-2 text-xs"
                  title="Lands once GSTN registration metadata is pulled · V1.x"
                >
                  — · V1.x
                </span>
              </span>
              {/* E-invoice mandate flag — V1.x (no IRP-onboarding state). */}
              <span className="inline-flex px-2 py-0.5 bg-bg text-muted rounded-sm text-xs font-semibold border border-dashed border-line-2">
                E-invoice · V1.x
              </span>
            </div>
            <div className="inline-flex gap-1.5 flex-shrink-0">
              <DisabledButton tone="mini" tooltip="GSTN portal SSO lands in V1.x">
                View on portal
              </DisabledButton>
              <DisabledButton tone="mini" tooltip="Scheme change lands in V1.x">
                Change scheme
              </DisabledButton>
            </div>
          </div>
        ) : null}

        {/* Period strip */}
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className="inline-flex bg-paper border border-line rounded-sm p-[3px] gap-[1px]"
            role="tablist"
            aria-label="Period"
          >
            {months.map((m) => {
              const isOn = m.period === period;
              return (
                <button
                  key={m.period}
                  type="button"
                  role="tab"
                  aria-selected={isOn}
                  onClick={() => setPeriod(m.period)}
                  className={`px-2.5 py-1.5 rounded-sm text-xs font-semibold flex flex-col items-center gap-[1px] leading-[1.1] min-w-[56px] ${
                    isOn
                      ? "bg-sky-50 text-sky-700 shadow-[inset_0_-2px_0_var(--color-sky-600,#0284C7)]"
                      : "text-muted hover:bg-bg hover:text-ink-2"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <span
                      className={`inline-block w-[6px] h-[6px] rounded-full ${m.dotClass(isOn)}`}
                      aria-hidden
                    />
                    {m.shortLabel}
                  </span>
                  <span
                    className={`text-xs uppercase tracking-wide ${isOn ? "text-sky-600" : "text-muted"} font-medium`}
                  >
                    {m.status}
                  </span>
                </button>
              );
            })}
          </div>
          {data ? (
            <span className="text-xs text-muted">
              Period <strong className="text-ink font-semibold">{monthLabel(period)}</strong> ·
              GSTR-3B due <span className="text-rose-700 font-bold">{formatDate(data.dueOn)}</span>
            </span>
          ) : null}
        </div>

        {/* KPI strip — mirrors `prototype/compliance/gst-returns.html:1009-1043`.
            Tile 1 (Net payable) and Tile 2 (ITC available) are real. Tile 3
            (E-invoices) and Tile 4 (On-time score · FY) are V1.x. */}
        {data ? (
          <section
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
            aria-label="GST summary"
          >
            <KpiTile
              accent={BigInt(data.totals.netCashPayablePaise) >= 0n ? "rose" : "emerald"}
              label={
                BigInt(data.totals.netCashPayablePaise) >= 0n
                  ? `Net payable · ${monthShortName(Number(period.split("-")[1] ?? "0"))}`
                  : "ITC carry-forward"
              }
              value={formatRupeesShort(absBigInt(BigInt(data.totals.netCashPayablePaise)))}
              meta={
                BigInt(data.totals.netCashPayablePaise) >= 0n
                  ? `Output − Input ITC · pay by ${formatDate(data.dueOn)}`
                  : "ITC exceeds output — credits next period"
              }
              icon={<TrendingUp size={13} strokeWidth={2} />}
            />
            <KpiTile
              accent="emerald"
              label="ITC available"
              value={formatRupeesShort(BigInt(data.totals.inputItcPaise))}
              meta={`${data.totals.billCount} ${data.totals.billCount === 1 ? "bill" : "bills"} aggregated · 2B match V1.x`}
              icon={<ShieldCheck size={13} strokeWidth={2} />}
            />
            <KpiTile
              accent="indigo"
              label={`E-invoices · ${monthShortName(Number(period.split("-")[1] ?? "0"))}`}
              value={data.totals.invoiceCount.toString()}
              valueUnit="invoices"
              meta={
                <span className="inline-flex items-center gap-1.5">
                  <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted">
                    V1.x
                  </span>
                  IRP signing lights up with NIC adapter
                </span>
              }
              icon={<Receipt size={13} strokeWidth={2} />}
              muted
            />
            <KpiTile
              accent="emerald"
              label="On-time score · FY"
              value="—"
              meta={
                <span className="inline-flex items-center gap-1.5">
                  <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted">
                    V1.x
                  </span>
                  Tracks once GSTN sync lands
                </span>
              }
              icon={<Clock3 size={13} strokeWidth={2} />}
              muted
            />
          </section>
        ) : null}

        {/* Coverage banner */}
        {data ? (
          <div className="flex gap-2.5 px-3.5 py-2.5 bg-sky-50 border border-sky-100 border-l-[3px] border-l-sky-600 rounded-sm text-xs text-sky-700 items-start">
            <Info size={14} strokeWidth={2} className="flex-shrink-0 mt-[1px]" />
            <span>
              <strong className="font-semibold">
                V1 covers the {data.lines.length} GSTR-3B lines below.
              </strong>{" "}
              For exports / SEZ / vendor credit notes (Tables {data.unsupportedCodes.join(", ")}),
              copy the figures directly from the portal — those entities aren't tracked in Flint V1.
              Push-to-GSTN lands in V1.x; for now hand the export to your CA.
            </span>
          </div>
        ) : null}

        {/* Returns grid — V1.0 ships GSTR-3B; sibling returns are V1.x placeholders.
            Mirrors the 6-card grid in `prototype/compliance/gst-returns.html` (lines 1051-1248).
            We render 4: the real GSTR-3B card + 3 V1.x siblings. */}
        {data ? (
          <section
            className="grid grid-cols-1 lg:grid-cols-2 gap-3"
            aria-label="GST returns for this period"
          >
            <ReturnsGridCard
              tone="active"
              code="GSTR-3B"
              subtitle="Summary · Form GSTR-3B"
              periodLabel={`${monthLabel(period)} · monthly`}
              statusPill={{ label: "In progress", tone: "info" }}
              body={
                <>
                  <strong>{data.totals.invoiceCount} invoices</strong> +{" "}
                  <strong>{data.totals.billCount} bills</strong> aggregated · auto-prepared from
                  your sales + purchases ledger.
                </>
              }
              stats={[
                {
                  label: "Tax payable",
                  value: formatRupeesShort(BigInt(data.totals.outputGstPaise)),
                },
                {
                  label: "After ITC offset",
                  value: formatRupeesShort(
                    BigInt(data.totals.netCashPayablePaise) < 0n
                      ? 0n
                      : BigInt(data.totals.netCashPayablePaise),
                  ),
                },
              ]}
              footer={
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 size={12} strokeWidth={2} />
                    Due <strong className="text-rose-700">{formatDate(data.dueOn)}</strong> ·{" "}
                    <DaysToDue dueOn={data.dueOn} />
                  </span>
                  <a
                    href="#gst-breakdown"
                    className="text-xs text-sky-700 font-semibold hover:underline"
                  >
                    View breakdown ↓
                  </a>
                </>
              }
            />
            <ReturnsGridCard
              tone="v1x"
              code="GSTR-1"
              subtitle="Outward · Form GSTR-1"
              periodLabel={`${monthLabel(period)} · monthly`}
              statusPill={{ label: "V1.x", tone: "v1x" }}
              body={
                <>
                  Place-of-supply split, B2B/B2CL/B2CS sub-tables, HSN summary. Lands once GSTR-1
                  aggregation + JSON export ships.
                </>
              }
            />
            <ReturnsGridCard
              tone="v1x"
              code="GSTR-2B"
              subtitle="ITC · Form GSTR-2B"
              periodLabel={`${monthLabel(period)} · auto-drafted on 14th`}
              statusPill={{ label: "V1.x", tone: "v1x" }}
              body={
                <>
                  Pulled monthly from GSTN. Reconciles with your purchase register so mismatched ITC
                  stays flagged before filing 3B.
                </>
              }
            />
            <ReturnsGridCard
              tone="v1x"
              code="GSTR-9"
              subtitle="Annual · Form GSTR-9"
              periodLabel="FY · annual"
              statusPill={{ label: "V1.x", tone: "v1x" }}
              body={
                <>
                  Auto-prepares from 12 monthly returns. Surfaces mismatches between annual and
                  monthly figures before December's deadline.
                </>
              }
            />
          </section>
        ) : null}

        {/* Breakdown table */}
        {summary.isLoading ? (
          <p className="text-sm text-muted">Loading GSTR-3B summary…</p>
        ) : summary.error ? (
          <Alert intent="bad" title="Couldn't load summary" description={summary.error.message} />
        ) : data ? (
          <section
            id="gst-breakdown"
            className="bg-paper border border-line rounded-md shadow-sm overflow-hidden scroll-mt-4"
            aria-label="GSTR-3B breakdown"
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-line">
              <span className="text-sm font-semibold text-ink">
                GSTR-3B · {monthLabel(period)} · auto-prepared from invoices + bills
              </span>
              <span className="text-xs text-muted">
                Source rows:{" "}
                <strong className="text-ink">
                  {data.totals.invoiceCount + data.totals.billCount}
                </strong>
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-bg">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-line">
                  <th className="px-3 py-2.5 font-semibold w-[80px]">Line</th>
                  <th className="px-3 py-2.5 font-semibold">Description</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Count</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Taxable</th>
                  <th className="px-3 py-2.5 font-semibold text-right">IGST</th>
                  <th className="px-3 py-2.5 font-semibold text-right">CGST</th>
                  <th className="px-3 py-2.5 font-semibold text-right">SGST</th>
                  <th className="px-3 py-2.5 font-semibold w-[110px]">Source</th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line) => (
                  <LineRow key={line.code} line={line} dueOn={data.dueOn} />
                ))}
                {/* Unsupported lines surfaced as muted rows. */}
                {data.unsupportedCodes.map((code) => (
                  <UnsupportedRow key={code} code={code} />
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-line text-xs text-muted gap-3 flex-wrap">
              <div className="flex gap-4 flex-wrap">
                <span>
                  Output GST ·{" "}
                  <strong className="text-ink font-semibold">
                    {formatRupees(BigInt(data.totals.outputGstPaise))}
                  </strong>
                </span>
                <span>
                  Input ITC ·{" "}
                  <strong className="text-ink font-semibold">
                    {formatRupees(BigInt(data.totals.inputItcPaise))}
                  </strong>
                </span>
                <span
                  className={
                    BigInt(data.totals.netCashPayablePaise) >= 0n
                      ? "text-rose-700 font-bold"
                      : "text-emerald-700 font-bold"
                  }
                >
                  {BigInt(data.totals.netCashPayablePaise) >= 0n
                    ? "Net cash payable"
                    : "ITC carry-forward"}{" "}
                  · {formatRupees(absBigInt(BigInt(data.totals.netCashPayablePaise)))}
                </span>
              </div>
              <button
                type="button"
                disabled
                title="Mark filed lands in V1.x"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-semibold border border-line bg-paper text-ink-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark filed
              </button>
            </div>
          </section>
        ) : null}

        {/* ITC reconciliation + right-rail aside — mirrors
            `prototype/compliance/gst-returns.html:1251-1454`. ITC reconciliation is
            V1.x (no GSTR-2B pull yet); the tax summary panel is real
            (built from the breakdown totals); E-invoice strip and HSN
            summary are V1.x. */}
        {data ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)] gap-3">
            <ItcReconciliationCard />
            <aside className="flex flex-col gap-3">
              <EInvoiceStrip invoiceCount={data.totals.invoiceCount} />
              <TaxSummaryPanel data={data} />
              <HsnSummaryPanel />
            </aside>
          </div>
        ) : null}

        {/* Filing history strip — V1.x; needs `gst_filings` table. */}
        {data ? <FilingHistory /> : null}
      </div>
    </WorkspaceShell>
  );
}

// ── ITC reconciliation card (V1.x) ───────────────────────────────

/**
 * ITC reconciliation — mirrors `prototype/compliance/gst-returns.html:1259-1375`.
 * Compares GSTR-2B (vendor-side) with the purchase register (our bills)
 * and flags mismatches. V1.0 has no GSTR-2B pull, so the tabs + rows
 * render disabled with explanatory copy.
 */
function ItcReconciliationCard() {
  return (
    <section
      className="bg-paper border border-line rounded-md shadow-sm"
      aria-label="ITC reconciliation"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line flex-wrap gap-2">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          ITC reconciliation · GSTR-2B vs Purchase register
          <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </span>
        <span className="text-xs text-muted">Auto-pulled monthly · 14th of each month</span>
      </header>
      {/* Tabs row — all disabled */}
      <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-line opacity-60">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
          <CheckCircle2 size={11} strokeWidth={2.4} />
          Matched
          <span className="px-1 rounded-full bg-emerald-100 text-xs font-bold">—</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
          <AlertTriangle size={11} strokeWidth={2.4} />
          Mismatch
          <span className="px-1 rounded-full bg-amber-100 text-xs font-bold">—</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-100">
          Missing in 2B
          <span className="px-1 rounded-full bg-rose-100 text-xs font-bold">—</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-paper text-muted border border-line">
          Excess in 2B
          <span className="px-1 rounded-full bg-bg text-xs font-bold">—</span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-paper text-muted border border-line">
          ITC blocked u/s 17(5)
          <span className="px-1 rounded-full bg-bg text-xs font-bold">—</span>
        </span>
      </div>
      {/* Placeholder row */}
      <div className="px-5 py-8 bg-bg/30 text-center text-xs text-muted">
        <div className="font-semibold text-ink-2 mb-1">GSTR-2B pull not connected yet.</div>
        <p className="m-0 leading-relaxed">
          When GSTN sync lands, Flint will pull GSTR-2B every 14th and reconcile each vendor's tax
          invoice against your booked bills · mismatch &gt; ₹100 escalates to you · WhatsApp the
          vendor to fix.
        </p>
      </div>
      <footer className="px-4 py-2.5 border-t border-line bg-bg text-xs text-muted flex items-center justify-between flex-wrap gap-2">
        <span>Lands with the V1.x GSTN sync adapter.</span>
      </footer>
    </section>
  );
}

// ── E-invoice strip (V1.x) ───────────────────────────────────────

/**
 * E-invoice live strip — mirrors `prototype/compliance/gst-returns.html:1380-1398`.
 * Shows IRN-signing status from the IRP (Invoice Registration Portal).
 * V1.0 doesn't sign through NIC IRP yet — the strip renders the chrome
 * with the real invoice count but flags IRN signing as V1.x.
 */
function EInvoiceStrip({ invoiceCount }: { invoiceCount: number }) {
  return (
    <div className="flex items-center gap-3 px-3.5 py-3 bg-sky-50/50 border border-sky-100 rounded-md text-xs text-ink-2">
      <span className="inline-grid place-items-center w-9 h-9 rounded-sm bg-sky-50 text-sky-600 flex-shrink-0">
        <Receipt size={16} strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink font-semibold inline-flex items-center gap-2">
          E-invoice · IRP signing
          <span className="px-1.5 py-px rounded-sm bg-paper text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </div>
        <div className="mt-1 inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
          <span>
            <span className="num text-ink font-semibold">{invoiceCount}</span> invoices in period
          </span>
          <Sep />
          <span>IRN signing lights up once NIC IRP adapter ships</span>
        </div>
      </div>
    </div>
  );
}

// ── Tax summary panel (real) ─────────────────────────────────────

/**
 * Tax summary — mirrors `prototype/compliance/gst-returns.html:1400-1421`. Real
 * computation from the breakdown lines. Sums CGST + SGST + IGST output
 * separately for the line-by-line view, then nets with ITC. Same paise
 * as the KPI tile; this panel breaks it open by component.
 */
function TaxSummaryPanel({ data }: { data: gstReturnsContract.GetMonthSummaryOutput }) {
  // Pull the 3.1(a) line for CGST/SGST/IGST output breakout.
  const out = data.lines.find((l) => l.code === "3.1(a)");
  const itc = data.lines.find((l) => l.code === "4(A)(5)");
  const cgstOut = out ? BigInt(out.cgstPaise) : 0n;
  const sgstOut = out ? BigInt(out.sgstPaise) : 0n;
  const igstOut = out ? BigInt(out.igstPaise) : 0n;
  const taxablePaise = out ? BigInt(out.taxablePaise) : 0n;
  const totalOutput = cgstOut + sgstOut + igstOut;
  const itcTotal = itc ? BigInt(itc.cgstPaise) + BigInt(itc.sgstPaise) + BigInt(itc.igstPaise) : 0n;
  const netPayable = totalOutput - itcTotal;

  return (
    <section className="bg-paper border border-line rounded-md shadow-sm" aria-label="Tax summary">
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-1.5">
          <PieChart size={14} strokeWidth={2} />
          Tax summary
        </span>
        <span className="text-xs text-muted">CGST + SGST + IGST</span>
      </header>
      <div className="px-4 py-3 flex flex-col gap-1.5 text-xs">
        <SummaryRow label="Outward taxable value" value={formatRupeesShort(taxablePaise)} />
        <SummaryRow label="CGST output (9%)" value={formatRupeesShort(cgstOut)} />
        <SummaryRow label="SGST output (9%)" value={formatRupeesShort(sgstOut)} />
        <SummaryRow label="IGST output (interstate)" value={formatRupeesShort(igstOut)} />
        <div className="border-t border-line pt-2 mt-1">
          <SummaryRow label="Tax collected" value={formatRupeesShort(totalOutput)} bold />
        </div>
        <SummaryRow
          label="Less · ITC available"
          value={`−${formatRupeesShort(itcTotal)}`}
          tone="good"
        />
        <div className="border-t border-line pt-2 mt-1">
          <SummaryRow
            label="Net payable"
            value={formatRupeesShort(netPayable < 0n ? -netPayable : netPayable)}
            tone={netPayable >= 0n ? "bad" : "good"}
            bold
            big
          />
        </div>
        <p className="m-0 mt-2 text-xs text-muted leading-relaxed">
          Pay via PMT-06 challan on or before{" "}
          <strong className="text-ink">{formatDate(data.dueOn)}</strong>. Cash ledger / challan
          generation lands in V1.x.
        </p>
      </div>
    </section>
  );
}

function SummaryRow({
  label,
  value,
  bold,
  big,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  big?: boolean;
  tone?: "good" | "bad" | "warn";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-700"
      : tone === "bad"
        ? "text-rose-700"
        : tone === "warn"
          ? "text-amber-700"
          : "text-ink";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={bold ? "text-ink-2 font-semibold" : "text-muted"}>{label}</span>
      <span className={`num ${toneClass} ${bold ? "font-bold" : ""} ${big ? "text-base" : ""}`}>
        {value}
      </span>
    </div>
  );
}

// ── HSN summary mini-table (V1.x) ────────────────────────────────

/**
 * HSN summary — mirrors `prototype/compliance/gst-returns.html:1424-1451`. Per-HSN
 * code aggregate that the portal's HSN summary section needs. V1.0
 * doesn't aggregate by HSN (the breakdown is by GST-line, not commodity);
 * V1.x adds an `items.hsn` → invoice_lines join.
 */
function HsnSummaryPanel() {
  return (
    <section className="bg-paper border border-line rounded-md shadow-sm" aria-label="HSN summary">
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          HSN summary
          <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </span>
        <span className="text-xs text-muted">Top HSN codes</span>
      </header>
      <div className="px-5 py-6 bg-bg/30 text-center text-xs text-muted">
        <Minus size={16} strokeWidth={2} className="mx-auto text-line-2 mb-1" />
        <p className="m-0 leading-relaxed">
          Per-HSN aggregate (taxable value + tax + units) lands once the
          <code className="font-mono bg-bg px-1 rounded mx-1 text-xs">items.hsn</code>
          column wires into the line-level rollup.
        </p>
      </div>
    </section>
  );
}

// ── Filing history strip (V1.x) ──────────────────────────────────

/**
 * 12-month filing history — mirrors `prototype/compliance/gst-returns.html:1466-1488`.
 * Two pips per month (GSTR-1 + GSTR-3B). V1.0 has no `gst_filings` table
 * yet; the strip renders the 12 months as "Pending · V1.x" so the chrome
 * is visible.
 */
function FilingHistory() {
  // Build the last 12 months ending at the current month (clock-driven).
  const now = new Date();
  const months: { label: string; isCurrent: boolean }[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: `${monthShortName(d.getMonth() + 1)} ${String(d.getFullYear()).slice(-2)}`,
      isCurrent: i === 0,
    });
  }
  return (
    <section
      className="bg-paper border border-line rounded-md shadow-sm"
      aria-label="Filing history"
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-line flex-wrap gap-2">
        <span className="text-sm font-semibold text-ink inline-flex items-center gap-2">
          <Calendar size={14} strokeWidth={2} />
          Filing history · last 12 months
          <span className="px-1.5 py-px rounded-sm bg-bg text-xs font-bold uppercase tracking-wider text-muted border border-line">
            V1.x
          </span>
        </span>
        <span className="inline-flex items-center gap-3 text-xs text-muted">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-emerald-600" aria-hidden /> Filed on time
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-amber-600" aria-hidden /> Late
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm bg-line-2" aria-hidden /> Pending
          </span>
        </span>
      </header>
      <div className="px-4 py-3">
        <div className="flex items-baseline gap-4 text-xs text-muted mb-3 flex-wrap">
          <span>
            <strong className="text-ink-2 font-bold">— / 12</strong> GSTR-1 filed
          </span>
          <span>
            <strong className="text-ink-2 font-bold">— / 12</strong> GSTR-3B filed
          </span>
          <span className="ml-auto text-muted">
            Compliance score lands with V1.x filing tracker
          </span>
        </div>
        <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
          {months.map((m) => (
            <div
              key={m.label}
              className={`text-center text-xs font-semibold rounded-sm py-2 ${
                m.isCurrent
                  ? "bg-sky-50 text-sky-700 border border-sky-100"
                  : "bg-bg text-muted border border-dashed border-line-2"
              }`}
            >
              <div className="whitespace-nowrap">{m.label}</div>
              <div className="mt-1 inline-flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-sm bg-line-2" aria-hidden />
                <span className="w-1.5 h-1.5 rounded-sm bg-line-2" aria-hidden />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted m-0">
          Two pips per month · GSTR-1 (left) and GSTR-3B (right) · click any month to drill in
          (V1.x).
        </p>
      </div>
    </section>
  );
}

// ── DisabledButton helper (shared shape with /tds) ───────────────

function DisabledButton({
  children,
  tooltip,
  tone,
}: {
  children: React.ReactNode;
  tooltip: string;
  tone: "primary" | "secondary" | "mini";
}) {
  const sizeAndShape =
    tone === "mini"
      ? "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-medium"
      : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold";
  const skin =
    tone === "primary"
      ? "bg-ink text-paper border border-ink"
      : "bg-paper text-ink-2 border border-line";
  return (
    <button
      type="button"
      disabled
      title={tooltip}
      className={`${sizeAndShape} ${skin} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

// ── Returns grid card ─────────────────────────────────────────────

interface ReturnsGridStatusPill {
  label: string;
  tone: "info" | "good" | "idle" | "v1x";
}

interface ReturnsGridStat {
  label: string;
  value: string;
}

interface ReturnsGridCardProps {
  /** `'active'` lights up the card; `'v1x'` mutes everything. */
  tone: "active" | "v1x";
  code: string;
  subtitle: string;
  periodLabel: string;
  statusPill: ReturnsGridStatusPill;
  body: React.ReactNode;
  /** When omitted (V1.x cards), the stats row is hidden. */
  stats?: ReturnsGridStat[];
  /** When omitted (V1.x cards), the footer is hidden. */
  footer?: React.ReactNode;
}

function ReturnsGridCard({
  tone,
  code,
  subtitle,
  periodLabel,
  statusPill,
  body,
  stats,
  footer,
}: ReturnsGridCardProps) {
  const pillSkin = (() => {
    switch (statusPill.tone) {
      case "info":
        return "bg-sky-50 text-sky-700 border-sky-100";
      case "good":
        return "bg-emerald-50 text-emerald-700 border-emerald-100";
      case "v1x":
        return "bg-paper text-muted border-line";
      default:
        return "bg-bg text-muted border-line";
    }
  })();
  const cardSkin =
    tone === "active"
      ? "bg-paper border-line shadow-sm border-l-[3px] border-l-sky-600"
      : "bg-bg border-dashed border-line-2 opacity-90";
  return (
    <article className={`rounded-md border ${cardSkin} p-4 flex flex-col gap-2.5`}>
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <strong className="text-base font-bold tracking-tight text-ink leading-none">
              {code}
            </strong>
            <span className="text-xs text-muted">{subtitle}</span>
          </div>
          <div className="text-xs text-muted mt-1">{periodLabel}</div>
        </div>
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${pillSkin}`}
        >
          {statusPill.label}
        </span>
      </header>
      <p className="text-xs text-muted leading-relaxed m-0">{body}</p>
      {stats && stats.length > 0 ? (
        <div className="flex gap-4 pt-2 border-t border-line">
          {stats.map((s) => (
            <div key={s.label} className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-muted font-semibold">
                {s.label}
              </div>
              <div className="text-lg font-bold tracking-tight num text-ink mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>
      ) : null}
      {footer ? (
        <div className="flex items-center justify-between text-xs text-muted pt-2 border-t border-line flex-wrap gap-2">
          {footer}
        </div>
      ) : null}
    </article>
  );
}

// ── Row renderers ─────────────────────────────────────────────────

interface LineRowProps {
  line: {
    code: string;
    label: string;
    hint: string;
    count: number;
    taxablePaise: string;
    igstPaise: string;
    cgstPaise: string;
    sgstPaise: string;
    sourceEntityType: "invoice" | "bill" | null;
    sourceEntityIds: readonly string[];
  };
  /** ISO date (`YYYY-MM-DD`) of the GSTR-3B filing deadline for this period. */
  dueOn: string;
}

function LineRow({ line, dueOn }: LineRowProps) {
  const isSummary = line.code === "6.1";
  return (
    <tr
      className={
        isSummary ? "border-t-2 border-ink-2 bg-bg font-bold" : "border-b border-line hover:bg-bg"
      }
    >
      <td className="px-3 py-3 align-middle">
        <span
          className={`inline-block px-1.5 py-0.5 rounded-sm font-mono text-xs font-bold ${
            isSummary ? "bg-ink text-paper" : "bg-bg text-ink"
          }`}
        >
          {line.code}
        </span>
      </td>
      <td className="px-3 py-3 align-middle">
        <span
          className={`block ${isSummary ? "text-sm text-ink font-bold" : "font-semibold text-ink"}`}
        >
          {line.label}
        </span>
        <span className={`text-xs ${isSummary ? "text-ink-2" : "text-muted"}`}>{line.hint}</span>
      </td>
      <td className="px-3 py-3 align-middle text-right num">{line.count > 0 ? line.count : "—"}</td>
      <td className="px-3 py-3 align-middle text-right num">
        {formatRupeesSigned(BigInt(line.taxablePaise))}
      </td>
      <td className="px-3 py-3 align-middle text-right num">
        {formatRupeesSigned(BigInt(line.igstPaise))}
      </td>
      <td className="px-3 py-3 align-middle text-right num">
        {formatRupeesSigned(BigInt(line.cgstPaise))}
      </td>
      <td className="px-3 py-3 align-middle text-right num">
        {formatRupeesSigned(BigInt(line.sgstPaise))}
      </td>
      <td className="px-3 py-3 align-middle">
        {line.count > 0 && line.sourceEntityType ? (
          <Link
            to={line.sourceEntityType === "invoice" ? "/sales/invoices" : "/procurement/bills"}
            className="text-xs text-sky-700 font-semibold hover:underline"
          >
            {line.count} {line.sourceEntityType}
            {line.count === 1 ? "" : "s"} →
          </Link>
        ) : isSummary ? (
          <span className="text-xs text-rose-700 font-bold uppercase tracking-wide">
            Pay by {formatDate(dueOn)}
          </span>
        ) : line.code === "3.1(d)" ? (
          <span className="text-xs text-muted">ADR-0014</span>
        ) : (
          <span className="text-xs text-muted">None this period</span>
        )}
      </td>
    </tr>
  );
}

function UnsupportedRow({ code }: { code: string }) {
  const description =
    code === "3.1(b)"
      ? {
          label: "Outward taxable supplies (zero-rated)",
          hint: "Not tracked in V1 — copy from the portal if exports / SEZ apply",
        }
      : code === "3.1(e)"
        ? {
            label: "Non-GST outward supplies",
            hint: "Not tracked in V1 — copy from the portal if applicable",
          }
        : { label: "ITC reversed", hint: "Vendor credit notes — not tracked in V1" };
  return (
    <tr className="bg-bg">
      <td className="px-3 py-3 align-middle">
        <span className="inline-block px-1.5 py-0.5 rounded-sm font-mono text-xs font-bold bg-paper text-muted">
          {code}
        </span>
      </td>
      <td className="px-3 py-3 align-middle">
        <span className="block text-muted">{description.label}</span>
        <span className="text-xs text-muted">{description.hint}</span>
      </td>
      <td className="px-3 py-3 align-middle text-right text-muted">—</td>
      <td className="px-3 py-3 align-middle text-right text-muted">—</td>
      <td className="px-3 py-3 align-middle text-right text-muted">—</td>
      <td className="px-3 py-3 align-middle text-right text-muted">—</td>
      <td className="px-3 py-3 align-middle text-right text-muted">—</td>
      <td className="px-3 py-3 align-middle text-xs text-muted">V1.x</td>
    </tr>
  );
}

// ── tiles + bits ──────────────────────────────────────────────────

type Accent = "indigo" | "emerald" | "amber" | "rose";

const ACCENT: Record<Accent, { stripe: string; iconBg: string; iconText: string }> = {
  indigo: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconText: "text-sky-600" },
  emerald: { stripe: "bg-emerald-600", iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  amber: { stripe: "bg-amber-600", iconBg: "bg-amber-50", iconText: "text-amber-700" },
  rose: { stripe: "bg-rose-600", iconBg: "bg-rose-50", iconText: "text-rose-600" },
};

interface KpiTileProps {
  accent: Accent;
  label: string;
  value: string;
  valueUnit?: string | undefined;
  meta?: React.ReactNode;
  icon: React.ReactNode;
  /** Render greyed out (V1.x placeholder values). */
  muted?: boolean | undefined;
}

function KpiTile({ accent, label, value, valueUnit, meta, icon, muted }: KpiTileProps) {
  void muted;
  const a = ACCENT[accent];
  return (
    <div className="relative bg-paper border border-line rounded-md shadow-sm px-4 pt-3.5 pb-3 overflow-hidden transition-all hover:-translate-y-px hover:shadow-md hover:border-line-2">
      <span className={`absolute top-0 left-0 bottom-0 w-[3px] ${a.stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted truncate">
          {label}
        </span>
        <span
          className={`inline-grid place-items-center w-[22px] h-[22px] rounded-sm ${a.iconBg} ${a.iconText}`}
        >
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold tracking-tight leading-none">
        <span className="num">{value}</span>
        {valueUnit ? (
          <span className="text-xs font-semibold text-muted ml-1">{valueUnit}</span>
        ) : null}
      </div>
      {meta ? (
        <div className="mt-1 text-xs text-muted flex items-center gap-1.5 flex-wrap">{meta}</div>
      ) : null}
    </div>
  );
}

function Sep() {
  return <span className="inline-block w-[3px] h-[3px] rounded-full bg-line-2" aria-hidden />;
}

function DaysToDue({ dueOn }: { dueOn: string }) {
  const days = daysToDue(dueOn);
  if (days < 0)
    return <span className="font-bold text-rose-700">{Math.abs(days)} days overdue</span>;
  if (days === 0) return <span className="font-bold text-rose-700">due today</span>;
  return <span>{days} days left</span>;
}

// ── helpers ──────────────────────────────────────────────────────

interface MonthCell {
  period: string;
  shortLabel: string;
  status: string;
  dotClass: (isOn: boolean) => string;
}

/** Build 6-month strip ending at `centerPeriod` (inclusive). */
function buildMonthStrip(centerPeriod: string): MonthCell[] {
  const [yStr, mStr] = centerPeriod.split("-");
  const baseYear = Number(yStr);
  const baseMonth = Number(mStr);
  const today = currentMonthIso();
  const cells: MonthCell[] = [];
  // 4 months back, the center, 1 month forward
  for (let offset = -4; offset <= 1; offset++) {
    let month = baseMonth + offset;
    let year = baseYear;
    while (month < 1) {
      month += 12;
      year -= 1;
    }
    while (month > 12) {
      month -= 12;
      year += 1;
    }
    const period = `${year}-${String(month).padStart(2, "0")}`;
    const isPast = period < today;
    const isFuture = period > today;
    cells.push({
      period,
      shortLabel: monthShortName(month),
      status: isFuture ? "Upcoming" : isPast ? "Closed" : "In prep",
      dotClass: (isOn: boolean) => {
        if (isOn) return "bg-sky-600";
        if (isFuture) return "bg-line-2";
        return "bg-emerald-600";
      },
    });
  }
  return cells;
}

function monthShortName(m: number): string {
  return (
    ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] ??
    ""
  );
}

function monthLabel(period: string): string {
  const [y, m] = period.split("-");
  return `${monthShortName(Number(m))} ${y}`;
}

function currentMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function daysToDue(dueOn: string): number {
  const due = new Date(`${dueOn}T00:00:00Z`);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - todayUtc) / (1000 * 60 * 60 * 24));
}

function absBigInt(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function formatRupees(paise: bigint): string {
  const sign = paise < 0n ? "−" : "";
  const rupees = absBigInt(paise) / 100n;
  return `${sign}₹${rupees.toLocaleString("en-IN")}`;
}

function formatRupeesSigned(paise: bigint): string {
  if (paise === 0n) return "₹0";
  return formatRupees(paise);
}

function formatRupeesShort(paise: bigint): string {
  const rupees = paise / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}L`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}
